/**
 * FirebaseAuthAdapter
 *
 * Firebase / Google Identity Platform implementation of IAuthAdapter.
 * Covers both plain Firebase Auth and multi-tenant Identity Platform (IDP)
 * via a single adapter — pass tenantId in config to enable IDP mode.
 *
 * All Firebase SDK calls are contained here. No other file should import
 * directly from "firebase/auth".
 *
 * confirmPasswordReset and sendPasswordResetEmail are routed through the
 * Photon backend so the frontend stays provider-agnostic.
 */

import {
  signInWithPopup,
  signOut,
  signInWithRedirect,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
} from 'firebase/auth';
import { IAuthAdapter } from '../interfaces/IAuthAdapter';
import { baseURL } from '../../../api/index';
import logger from '../../Logger';

/**
 * Build a Firebase provider instance from an SSO_PROVIDERS config entry.
 *
 * Supported id patterns:
 *   "google"        → GoogleAuthProvider
 *   "github.com"    → GithubAuthProvider
 *   "saml.*"        → SAMLAuthProvider  (redirect-only — enforced by Firebase SDK)
 *   anything else   → OAuthProvider(id) covers microsoft.com, apple.com, oidc.*, etc.
 *
 * @param {{ id: string, label: string, type: string }} providerConfig
 * @returns {import('firebase/auth').AuthProvider}
 */
function buildProvider({ id }) {
  if (id === 'google') return new GoogleAuthProvider();
  if (id === 'github.com') return new GithubAuthProvider();
  if (id.startsWith('saml.')) return new SAMLAuthProvider(id);
  return new OAuthProvider(id);
}

export class FirebaseAuthAdapter extends IAuthAdapter {
  constructor() {
    super();
    /** @type {import('firebase/auth').Auth | null} */
    this._auth = null;
    /**
     * Map of provider id → { instance, type, label } built from SSO_PROVIDERS config.
     * @type {Map<string, { instance: import('firebase/auth').AuthProvider, type: string, label: string }>}
     */
    this._providers = new Map();
  }

  /**
   * Initialise with the Firebase auth instance and SSO provider configs.
   * Called once from Secret.js after the Firebase app is set up.
   *
   * @param {{ auth: import('firebase/auth').Auth, ssoProviders?: Array<{id: string, label: string, type: string}> }} config
   */
  async initialize({ auth, ssoProviders = [] }) {
    this._auth = auth;
    this._providers = new Map(
      ssoProviders.map((cfg) => [cfg.id, { instance: buildProvider(cfg), type: cfg.type, label: cfg.label }])
    );
  }

  // ---------------------------------------------------------------------------
  // Sign-in methods
  // ---------------------------------------------------------------------------

  async signInWithEmailAndPassword(email, password) {
    if (!this._auth) throw new Error('Firebase auth is not configured. Use local login.');
    const result = await signInWithEmailAndPassword(this._auth, email, password);
    return result.user;
  }

  /**
   * Sign in with the provider identified by id.
   * SAML providers use redirect (SDK-enforced). All others try popup first,
   * falling back to redirect if the popup is blocked or unavailable.
   *
   * @param {string} providerId
   * @returns {Promise<import('firebase/auth').User | null>} user on popup success; null when redirect is initiated
   */
  async signInWithProvider(providerId) {
    const entry = this._providers.get(providerId);
    if (!entry) throw new Error(`SSO provider '${providerId}' is not configured.`);
    const { instance, type } = entry;

    if (type === 'saml') {
      await signInWithRedirect(this._auth, instance);
      return null;
    }

    try {
      const result = await signInWithPopup(this._auth, instance);
      return result.user;
    } catch (error) {
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        throw error; // let the caller handle user-cancelled vs blocked differently
      }
      // Any other popup failure — fall back to redirect
      await signInWithRedirect(this._auth, instance);
      return null;
    }
  }

  async sendSignInLinkToEmail(email, name) {
    const response = await fetch(`${baseURL}/api/auth/send-signin-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to send sign-in link');
    }
  }

  isSignInWithEmailLink(url) {
    if (!this._auth) return false;
    return isSignInWithEmailLink(this._auth, url);
  }

  async signInWithEmailLink(email, url) {
    if (!this._auth) throw new Error('Firebase auth is not configured.');
    const result = await signInWithEmailLink(this._auth, email, url);
    return result.user;
  }

  // ---------------------------------------------------------------------------
  // Password reset — routed through backend to stay provider-agnostic
  // ---------------------------------------------------------------------------

  /**
   * Request a password reset email via the backend.
   * The backend calls the provider Admin SDK so a branded reset link is sent.
   *
   * @param {string} email
   */
  async sendPasswordResetEmail(email) {
    const response = await fetch(`${baseURL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to send password reset email');
    }
  }

  /**
   * Confirm a password reset using the oobCode from the reset link.
   * Routes through the backend — no Firebase client SDK call needed.
   *
   * @param {string} oobCode
   * @param {string} newPassword
   */
  async confirmPasswordReset(oobCode, newPassword) {
    const response = await fetch(`${baseURL}/api/auth/confirm-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oobCode, newPassword }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to confirm password reset');
    }
  }

  // ---------------------------------------------------------------------------
  // Profile & token
  // ---------------------------------------------------------------------------

  async updateProfile(user, profile) {
    await updateProfile(user, profile);
  }

  async getIdToken(user, forceRefresh = false) {
    return user.getIdToken(forceRefresh);
  }

  // ---------------------------------------------------------------------------
  // Session lifecycle
  // ---------------------------------------------------------------------------

  onAuthStateChanged(callback) {
    if (!this._auth) {
      // Local auth mode — no Firebase auth instance; signal no Firebase user
      callback(null);
      return () => {};
    }
    return onAuthStateChanged(this._auth, callback);
  }

  async signOut() {
    try {
      await signOut(this._auth);
    } catch (error) {
      logger.warn('FirebaseAuthAdapter', 'signOut failed (may already be signed out)', {
        error: error.message,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // SSO provider registry
  // ---------------------------------------------------------------------------

  /**
   * Returns the full config for all configured SSO providers.
   * Used by the login page to render one button per provider.
   *
   * @returns {Array<{ id: string, label: string, type: string }>}
   */
  getSsoProviders() {
    return Array.from(this._providers.entries()).map(([id, { type, label }]) => ({ id, label, type }));
  }
}
